/**
 * FilingsTab - SEC filings list with type filters
 */
import { useState } from 'react'
import { FilingCard } from './FilingCard'
import { useSecFilings } from '../../hooks/useResearch'

interface FilingsTabProps {
  symbol: string
}

type FilingFilter = 'all' | '10-K' | '10-Q' | '8-K'

const FILTER_OPTIONS: { value: FilingFilter; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: '10-K', label: '10-K' },
  { value: '10-Q', label: '10-Q' },
  { value: '8-K', label: '8-K' },
]

function FilingSkeleton() {
  return (
    <div className="bg-white border border-gray-200 rounded-lg p-4 animate-pulse">
      <div className="flex items-center gap-3">
        <div className="h-6 w-12 bg-gray-200 rounded" />
        <div className="h-5 w-16 bg-gray-200 rounded" />
        <div className="flex-1" />
        <div className="h-5 w-24 bg-gray-200 rounded" />
      </div>
    </div>
  )
}

export function FilingsTab({ symbol }: FilingsTabProps) {
  const [filter, setFilter] = useState<FilingFilter>('all')

  const {
    data: filingsData,
    isLoading,
    error,
  } = useSecFilings(symbol, {
    filingTypes: filter === 'all' ? undefined : [filter],
    limit: 50,
  })

  const filings = filingsData?.filings ?? []

  return (
    <div className="space-y-6">
      {/* Filter buttons */}
      <div className="flex flex-wrap gap-2">
        {FILTER_OPTIONS.map((option) => (
          <button
            key={option.value}
            type="button"
            onClick={() => setFilter(option.value)}
            className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
              filter === option.value
                ? 'bg-primary-600 text-white shadow-sm'
                : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
            }`}
          >
            {option.label}
          </button>
        ))}
      </div>

      {/* Error state */}
      {error && (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 text-gray-600">
          No SEC filings available for this symbol.
        </div>
      )}

      {/* Loading state */}
      {isLoading && (
        <div className="space-y-4">
          {Array.from({ length: 5 }).map((_, i) => (
            <FilingSkeleton key={i} />
          ))}
        </div>
      )}

      {/* Filings list */}
      {!isLoading && !error && (
        <>
          {filings.length > 0 ? (
            <div className="space-y-4">
              {filings.map((filing) => (
                <FilingCard key={filing.id} filing={filing} />
              ))}
            </div>
          ) : (
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-8 text-center">
              <svg
                className="mx-auto h-12 w-12 text-gray-400"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                />
              </svg>
              <h3 className="mt-4 text-sm font-medium text-gray-900">No filings found</h3>
              <p className="mt-2 text-sm text-gray-500">
                {filter === 'all'
                  ? 'No SEC filings are available for this company.'
                  : `No ${filter} filings are available. Try selecting a different filter.`}
              </p>
            </div>
          )}

          {/* Results count */}
          {filings.length > 0 && (
            <p className="text-sm text-gray-500 text-center">
              Showing {filings.length} {filter === 'all' ? '' : filter} filing
              {filings.length !== 1 ? 's' : ''}
            </p>
          )}
        </>
      )}
    </div>
  )
}

export default FilingsTab
